import { useEffect, useMemo, useState } from "react";

import { usePageTitle } from "@/hooks/usePageTitle";
import {
  ADMIN_USERS_PAGE_SIZE,
  adminUsersPageCount,
  adminUsersResultRangeLabel,
} from "@/lib/api/admin-portal-user-list";
import {
  formatPortalUserDate,
  listAdminPortalUsers,
  portalUserStatusLabel,
  type AdminPortalUser,
  type PortalUserStatus,
} from "@/lib/api/admin-portal-users";
import { AdminShell } from "@/pages/admin/AdminShell";

type LoadState =
  | { kind: "loading" }
  | { kind: "refreshing" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

function StatusBadge({ status }: { status: PortalUserStatus }) {
  const label = portalUserStatusLabel(status);
  const cls = status === "verified" ? "s-approved" : "s-review";
  return (
    <span className={`status-badge ${cls}`}>
      <span className="s-dot" />
      {label}
    </span>
  );
}

export default function AdminUsersPage() {
  usePageTitle("Portal users — API Admin Panel");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | PortalUserStatus>("");
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<AdminPortalUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      await Promise.resolve();
      if (cancelled) {
        return;
      }

      setLoadState((prev) =>
        prev.kind === "ready" || prev.kind === "refreshing"
          ? { kind: "refreshing" }
          : { kind: "loading" },
      );

      try {
        const response = await listAdminPortalUsers({
          q: debouncedSearch || undefined,
          status: statusFilter || undefined,
          page,
          limit: ADMIN_USERS_PAGE_SIZE,
        });
        if (cancelled) {
          return;
        }
        setUsers(response.users);
        setTotal(response.total);
        setLoadState({ kind: "ready" });
      } catch {
        if (cancelled) {
          return;
        }
        setLoadState({
          kind: "error",
          message: "Could not load portal users. Try again.",
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, statusFilter, page, reloadKey]);

  const pageCount = useMemo(
    () => adminUsersPageCount(total, ADMIN_USERS_PAGE_SIZE),
    [total],
  );
  const safePage = Math.min(Math.max(1, page), pageCount);
  const listBusy = loadState.kind === "loading" || loadState.kind === "refreshing";
  const showRows = (loadState.kind === "ready" || loadState.kind === "refreshing") && users.length > 0;
  const hasActiveFilters =
    debouncedSearch.length > 0 || statusFilter !== "";

  function selectStatusFilter(next: "" | PortalUserStatus) {
    setStatusFilter(next);
    setPage(1);
  }

  function clearFilters() {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter("");
    setPage(1);
  }

  return (
    <AdminShell
      activeNav="users"
      title="Portal users"
      description="Registered applicants who can sign in and submit API access requests."
      testId="admin-users-page"
    >
      <div className="toolbar" data-testid="admin-users-toolbar">
        <div className="search-wrap">
          <i className="ti ti-search" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, email or company…"
            aria-label="Search portal users"
            data-testid="admin-users-search"
          />
        </div>
        <button
          type="button"
          className={`filter-btn${statusFilter === "" ? " on" : ""}`}
          aria-pressed={statusFilter === ""}
          data-testid="admin-users-status-all"
          onClick={() => selectStatusFilter("")}
        >
          All statuses
        </button>
        <button
          type="button"
          className={`filter-btn${statusFilter === "pending" ? " on" : ""}`}
          aria-pressed={statusFilter === "pending"}
          data-testid="admin-users-status-pending"
          onClick={() => selectStatusFilter("pending")}
        >
          Pending
        </button>
        <button
          type="button"
          className={`filter-btn${statusFilter === "active" ? " on" : ""}`}
          aria-pressed={statusFilter === "active"}
          data-testid="admin-users-status-active"
          onClick={() => selectStatusFilter("active")}
        >
          Active
        </button>
        <button
          type="button"
          className={`filter-btn${statusFilter === "verified" ? " on" : ""}`}
          aria-pressed={statusFilter === "verified"}
          data-testid="admin-users-status-verified"
          onClick={() => selectStatusFilter("verified")}
        >
          Verified
        </button>
      </div>

      <div className="results-meta" data-testid="admin-users-results-meta">
        <span>
          {adminUsersResultRangeLabel(safePage, ADMIN_USERS_PAGE_SIZE, total)}
        </span>
        {hasActiveFilters ? (
          <button
            type="button"
            className="results-clear"
            data-testid="admin-users-clear-filters"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <div className="tbl-wrap" data-testid="admin-users-table-wrap">
        <div
          className={`tbl-scroll${loadState.kind === "refreshing" ? " is-refreshing" : ""}`}
          data-testid="admin-users-list"
          aria-busy={listBusy}
        >
          {loadState.kind === "refreshing" ? (
            <div
              className="tbl-refresh"
              data-testid="admin-users-refreshing"
              role="status"
              aria-live="polite"
            >
              <i className="ti ti-loader-2" aria-hidden="true" />
              <span>Updating results…</span>
            </div>
          ) : null}

          {loadState.kind === "loading" ? (
            <div className="empty-state" data-testid="admin-users-loading">
              <i className="ti ti-loader-2" aria-hidden="true" />
              <p>Loading users…</p>
            </div>
          ) : null}

          {loadState.kind === "error" ? (
            <div className="empty-state" data-testid="admin-users-error">
              <i className="ti ti-alert-circle" aria-hidden="true" />
              <p>{loadState.message}</p>
              <button
                type="button"
                className="filter-btn on"
                onClick={() => setReloadKey((value) => value + 1)}
              >
                Retry
              </button>
            </div>
          ) : null}

          {loadState.kind === "ready" && users.length === 0 ? (
            <div className="empty-state" data-testid="admin-users-empty">
              <i className="ti ti-users" aria-hidden="true" />
              <p>No portal users match the current filters.</p>
            </div>
          ) : null}

          {showRows ? (
            <table className="req-table">
              <thead>
                <tr>
                  <th scope="col">Applicant</th>
                  <th scope="col">Business</th>
                  <th scope="col">Status</th>
                  <th scope="col">Requests</th>
                  <th scope="col">Registered</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="req-tr"
                    data-testid={`admin-user-row-${user.id}`}
                  >
                    <td>
                      <div className="req-name">{user.contact_name}</div>
                      <div className="req-email">{user.email}</div>
                    </td>
                    <td>
                      <div className="req-biz">{user.company_name}</div>
                    </td>
                    <td>
                      <StatusBadge status={user.status} />
                    </td>
                    <td>
                      <div className="req-vol">{user.access_request_count}</div>
                    </td>
                    <td>
                      <div className="req-date">
                        {formatPortalUserDate(user.created_at)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        {total > ADMIN_USERS_PAGE_SIZE ? (
          <div className="pager" data-testid="admin-users-pager">
            <button
              type="button"
              className="filter-btn"
              disabled={safePage <= 1 || listBusy}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              data-testid="admin-users-prev-page"
            >
              Previous
            </button>
            <span data-testid="admin-users-page-label">
              Page {safePage} of {pageCount}
            </span>
            <button
              type="button"
              className="filter-btn"
              disabled={safePage >= pageCount || listBusy}
              onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
              data-testid="admin-users-next-page"
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </AdminShell>
  );
}
